<template>
  <div ref="wrapperRef" class="multisel-wrapper">
    <MultiSelect
      ref="multiselectRef"
      :key="`${$attrs['field-id']}-multisel`"
      :class="{ 'is-danger': isInvalid || errorMessage || hasError }"
      :options="displayOptions"
      :custom-label="getLabel"
      :track-by="resolvedTrackBy"
      :tabindex="0"
      :placeholder="placeholder"
      :select-label="'↵'"
      :multiple="multiple"
      :deselect-label="'×'"
      :selected-label="'✓'"
      :tag-placeholder="'Create ↵'"
      :loading="loading || isLoadingOptions"
      :internal-search="!onSearch"
      :model-value="selectedValue"
      v-bind="$attrs"
      @search-change="onSearchChange"
      @update:model-value="onInput"
      @tag="onTag"
      @open="onOpen"
      @close="onClose"
    >
      <template #noResult>
        <!-- Loading is indicated by the progress bar in #beforeList and the
             built-in vue-multiselect spinner in the input (via :loading prop). -->
      </template>
      <template v-for="(_, name) in forwardedSlots" #[name]="slotData">
        <slot :name="name" v-bind="slotData" />
      </template>
      <!-- #noOptions: overridable; exposes { query }. Smart default when onSearch is set. -->
      <template #noOptions>
        <slot name="noOptions" :query="currentQuery">
          <span v-if="onSearch && !isLoadingOptions">
            <span v-if="!currentQuery">Start typing to search.</span>
            <span v-else>No results found.</span>
          </span>
        </slot>
      </template>
      <template #placeholder>
        {{ placeholder }}
      </template>
      <template #beforeList>
        <div v-if="isLoadingOptions" class="dropdown-progress-wrap">
          <ProgressBar />
        </div>
        <li
          v-if="!isLoadingOptions && showResultsCount"
          style="pointer-events: none"
        >
          <div class="dropdown-notice">
            Showing
            <span>
              <u>{{ rawOptions.length }} results</u>
            </span>
            out of {{ totalResults }}
            <span v-if="onSearch && lastSearch">
              for «
              <b>{{ lastSearch }}</b>
              »
            </span>
          </div>
        </li>
      </template>
      <template #option="{ option }">
        <!-- isTag: custom #option slot suppresses vue-multiselect's built-in tag template -->
        <span v-if="option.isTag" class="option-tag-create">
          {{ option.label }}
          <span class="option-tag-placeholder">Create ↵</span>
        </span>
        <slot v-else name="option" :option="option">
          <span class="option">
            <span v-if="isDeleted(option)" class="icon icon-opt-deleted">
              <i class="fa-regular fa-trash-can" />
            </span>
            {{ getLabel(option) }}
          </span>
        </slot>
      </template>
      <template #afterList>
        <li v-if="showRefineMessage">
          <div class="dropdown-notice">
            Refine your search to see other results
          </div>
        </li>
      </template>
    </MultiSelect>
    <p v-if="oneError" class="help is-danger">
      {{ oneError.$message }}
    </p>
    <p v-if="errorMessage" class="help is-danger">
      {{ errorMessage }}
    </p>
  </div>
</template>

<script>
import { onKeyStroke, useElementVisibility } from '@vueuse/core'
import { debounce } from 'lodash-es'
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'

import { useFieldValidation } from '@/composables/fields/useFieldValidation'
import {
  animateClose,
  animateOpen,
  getScrollableAncestors,
  MultiSelect,
  positionDropdownList as positionList,
  restoreListFromBody,
} from '@/libs/vue-multiselect/index.js'

// vue-multiselect requires objects when track-by is set; wrap bare strings.
function wrapString(val) {
  return typeof val === 'string'
    ? { id: val, label: val, _isString: true }
    : val
}

function unwrap(val) {
  if (val == null) return val
  if (Array.isArray(val)) return val.map(unwrap)
  return val._isString ? val.id : val
}

export default {
  name: 'SelectContainer',
  emits: ['input', 'search-change'],
  components: { MultiSelect },
  props: {
    value: { type: [String, Object, Array] },
    validationRules: { type: Array, default: () => [] },
    options: { type: Array, default: () => [] },
    multiple: { type: Boolean, default: false },
    label: { type: String, default: null },
    trackBy: { type: String, default: '' },
    loadOptions: { type: Function, default: null },
    onSearch: { type: Function, default: null },
    loading: { type: Boolean, default: false },
    errorMessage: { type: String },
    hasError: { type: Boolean, default: false },
  },
  setup(props, { attrs, emit, slots }) {
    const wrapperRef = ref(null)
    const multiselectRef = ref(null)
    let wheelTimer = null

    const fieldId = attrs['field-id']
    const validationScope = attrs['validation-scope']
    const defaultEmptyValue =
      attrs?.fieldMeta?.default !== undefined ? attrs.fieldMeta.default : null

    // ─── Label & track-by ───────────────────────────────────────────────
    const resolvedTrackBy = computed(() => props.trackBy || 'id')

    const LABEL_FALLBACKS = ['choice_label', 'label', 'name', 'id']

    function getLabel(opt) {
      if (!opt) return ''
      if (opt._isString) return opt.label
      if (props.label && opt[props.label]) return opt[props.label]
      for (const key of LABEL_FALLBACKS) {
        if (opt[key]) return opt[key]
      }
      return ''
    }

    function isDeleted(opt) {
      if (!opt || typeof opt !== 'object') return false
      return opt.deleted?.value !== undefined
        ? Boolean(opt.deleted.value)
        : Boolean(opt.deleted)
    }

    // ─── Options ────────────────────────────────────────────────────────
    const rawOptions = ref([])
    watch(
      () => props.options,
      (o) => {
        // Don't let empty prop defaults overwrite async-loaded options
        if (!o?.length && rawOptions.value.length > 0) return
        rawOptions.value = o
      },
      { immediate: true },
    )

    const wrappedOptions = computed(() => rawOptions.value.map(wrapString))

    // ─── Current value ──────────────────────────────────────────────────
    const inputValue = ref()

    // Validation (must be defined before watcher that uses v$)
    const { v$, isInvalid, oneError } = useFieldValidation(
      fieldId,
      inputValue,
      props.validationRules,
      validationScope,
    )

    watch(
      () => props.value,
      (newVal) => {
        inputValue.value = newVal
        v$?.value?.$touch()
      },
      { immediate: true },
    )

    const selectedValue = computed(() => {
      const val = inputValue.value
      if (val == null || val === '') return null

      if (typeof val === 'string') return wrapString(val)

      if (Array.isArray(val)) {
        const items = val.map(wrapString).filter(Boolean)
        return items.length ? items : null
      }

      // Object with no trackBy key and no label is effectively empty
      const trackKey = resolvedTrackBy.value
      if (!val[trackKey] && !getLabel(val)) return null
      return val
    })

    // ─── Display options: selected first, then rest sorted ──────────────
    const displayOptions = computed(() => {
      const trackKey = resolvedTrackBy.value
      const getId = (opt) => opt?.[trackKey]

      const selected = Array.isArray(selectedValue.value)
        ? selectedValue.value
        : selectedValue.value
          ? [selectedValue.value]
          : []
      const selectedIds = new Set(selected.map(getId))

      const selectedInOptions = wrappedOptions.value.filter((opt) =>
        selectedIds.has(getId(opt)),
      )
      const selectedInOptionsIds = new Set(selectedInOptions.map(getId))

      // selected values absent from current options (e.g. filtered out by search)
      const selectedNotInOptions = selected.filter(
        (v) => !selectedInOptionsIds.has(getId(v)),
      )

      const unselected = wrappedOptions.value
        .filter((opt) => !selectedIds.has(getId(opt)))
        .sort((a, b) => {
          const la = getLabel(a)
          const lb = getLabel(b)
          return la && lb ? la.localeCompare(lb) : -1
        })

      return [...selectedInOptions, ...selectedNotInOptions, ...unselected]
    })

    const placeholder = computed(() => {
      if (selectedValue.value) return getLabel(selectedValue.value)
      return attrs.placeholder || 'Select an option'
    })

    // ─── Async search / load ────────────────────────────────────────────
    const isLoadingOptions = ref(Boolean(props.loadOptions))
    const currentQuery = ref('')
    const lastSearch = ref('')

    // noOptions is handled explicitly below; exclude it from the forwarded set.
    const forwardedSlots = computed(() =>
      Object.fromEntries(
        Object.entries(slots).filter(([k]) => k !== 'noOptions'),
      ),
    )
    const totalResults = ref(0)
    const totalPages = ref(0)

    function processResults(data) {
      rawOptions.value = data.results
      totalResults.value = data.total
      totalPages.value = data.total_pages
    }

    let searchController = null

    async function doSearch(search) {
      if (!props.onSearch) return
      isLoadingOptions.value = true
      // Cancel any in-flight request from the previous keystroke.
      searchController?.abort()
      searchController = new AbortController()
      const { signal } = searchController
      try {
        const response = await props.onSearch(search, signal)
        if (response?.status === 200) {
          processResults(response.data)
          lastSearch.value = search
          hasLoadedInitialSearch.value = true
        }
      } catch (err) {
        // Axios reports an aborted request as CanceledError, not AbortError.
        if (!['AbortError', 'CanceledError'].includes(err.name))
          console.warn('SelectContainer: search failed', err)
      } finally {
        // A superseded keystroke must not clear the flag for the request that
        // replaced it.
        if (signal === searchController?.signal) isLoadingOptions.value = false
      }
    }

    async function doLoadOptions() {
      isLoadingOptions.value = true
      try {
        const response = await props.loadOptions()
        if (response.status === 200) processResults(response.data)
      } finally {
        isLoadingOptions.value = false
      }
    }

    const debouncedSearch = props.onSearch
      ? debounce(doSearch, 700, { leading: true, trailing: true })
      : (search) => {
          lastSearch.value = search
          emit('search-change', search)
        }

    const showResultsCount = computed(
      () =>
        totalResults.value > 0 &&
        totalPages.value > 1 &&
        (props.onSearch || !lastSearch.value),
    )
    const showRefineMessage = computed(
      () => !isLoadingOptions.value && totalPages.value > 1 && props.onSearch,
    )

    // ─── FLIP animation on option reorder ──────────────────────────────
    watch(
      displayOptions,
      async (newOpts, oldOpts) => {
        const list = multiselectRef.value?.$refs?.list
        if (!list) return
        const trackKey = resolvedTrackBy.value
        const snap = {}
        list.querySelectorAll('.multiselect__element').forEach((el, i) => {
          const id = oldOpts[i]?.[trackKey] ?? i
          snap[id] = el.getBoundingClientRect().top
        })
        await nextTick()
        list.querySelectorAll('.multiselect__element').forEach((el, i) => {
          const id = newOpts[i]?.[trackKey] ?? i
          const oldTop = snap[id]
          if (oldTop == null) return
          const dy = oldTop - el.getBoundingClientRect().top
          if (Math.abs(dy) < 1) return
          el.animate(
            [
              { transform: `translateY(${dy}px)` },
              { transform: 'translateY(0)' },
            ],
            { duration: 250, easing: 'cubic-bezier(0.4, 0, 0.2, 1)' },
          )
        })
      },
      { flush: 'pre' },
    )

    // ─── Event handlers ─────────────────────────────────────────────────
    function onInput(val) {
      emit('input', unwrap(val) ?? defaultEmptyValue)
      v$?.value?.$touch()
    }

    function onTag(searchText) {
      const newOption =
        props.label && props.trackBy
          ? { [props.label]: searchText, [props.trackBy]: searchText }
          : searchText
      rawOptions.value.unshift(newOption)
      // Emit the raw value (not wrapped) — onInput would double-unwrap
      emit('input', newOption)
      v$?.value?.$touch()
    }

    function onSearchChange(search) {
      currentQuery.value = search
      debouncedSearch(search)
    }

    const targetIsVisible = useElementVisibility(multiselectRef)

    function closeDropdown() {
      multiselectRef.value?.deactivate()
      multiselectRef.value?.$refs.search?.blur()
    }

    const hasLoadedInitialSearch = ref(false)

    // ─── Dropdown positioning ───────────────────────────────────────────

    function positionDropdownList(instant = false) {
      const ms = multiselectRef.value
      positionList(ms?.$el, ms?.$refs?.list, instant)
    }

    let scrollAncestors = []
    let listResizeObserver = null

    function onReposition() {
      positionDropdownList()
      clearTimeout(wheelTimer)
      wheelTimer = setTimeout(() => positionDropdownList(), 150)
    }

    function attachScrollListeners() {
      scrollAncestors = getScrollableAncestors(multiselectRef.value.$el)
      scrollAncestors.forEach((el) =>
        el.addEventListener('scroll', onReposition, { passive: true }),
      )
    }

    function detachScrollListeners() {
      scrollAncestors.forEach((el) =>
        el.removeEventListener('scroll', onReposition),
      )
      scrollAncestors = []
    }

    function onOpen() {
      // The flag is set by doSearch once the search actually succeeds — setting
      // it here would make a failed or aborted first search permanent, leaving
      // the dropdown empty on every subsequent open.
      if (props.onSearch && !hasLoadedInitialSearch.value) doSearch('')

      nextTick(() => {
        positionDropdownList(true)
        attachScrollListeners()
        const ms = multiselectRef.value
        const list = ms?.$refs?.list
        if (list) {
          listResizeObserver = new ResizeObserver(() => positionDropdownList())
          listResizeObserver.observe(list)
          animateOpen(ms.$el, list)
        }
      })
    }

    function onClose() {
      searchController?.abort()
      isLoadingOptions.value = false
      listResizeObserver?.disconnect()
      listResizeObserver = null
      detachScrollListeners()
      const list = multiselectRef.value?.$refs?.list
      if (list) animateClose(list)
    }

    // ─── Lifecycle ──────────────────────────────────────────────────────
    onKeyStroke('Escape', async () => {
      await nextTick()
      closeDropdown()
    })

    watch(targetIsVisible, (visible) => {
      if (!visible) closeDropdown()
    })

    onMounted(() => {
      if (props.loadOptions) doLoadOptions()
      window.addEventListener('wheel', onReposition)
    })

    onUnmounted(() => {
      clearTimeout(wheelTimer)
      debouncedSearch.cancel?.()
      listResizeObserver?.disconnect()
      window.removeEventListener('wheel', onReposition)
      detachScrollListeners()
      const list = multiselectRef.value?.$refs?.list
      if (list) restoreListFromBody(list)
    })

    return {
      wrapperRef,
      multiselectRef,
      rawOptions,
      displayOptions,
      selectedValue,
      resolvedTrackBy,
      getLabel,
      isDeleted,
      placeholder,
      v$,
      isInvalid,
      oneError,
      isLoadingOptions,
      currentQuery,
      forwardedSlots,
      lastSearch,
      totalResults,
      totalPages,
      showResultsCount,
      showRefineMessage,
      onInput,
      onTag,
      onSearchChange,
      onOpen,
      onClose,
    }
  },
}
</script>

<style lang="scss" scoped>
.multisel-wrapper {
  max-width: 100%;
  position: relative;
}

.dropdown-progress-wrap {
  position: relative;
  height: 4px;
}

span.option:has(.icon-opt-deleted) {
  text-decoration: line-through;
  text-decoration-color: var(--color-danger);
  .icon-opt-deleted {
    color: var(--color-danger);
  }
}

.dropdown-notice {
  padding: var(--space-2) var(--space-3);
  background-color: var(--brand-subtle);
  color: var(--brand-text);
  font-size: var(--text-xs);
  font-weight: 600;
}

:deep(.multiselect__content) {
  /* Force content list to block display at 100% width (instead of inline-block with min-width) */
  display: block !important;
  width: 100% !important;
  min-width: 0 !important;
}

:deep(.multiselect__option) {
  /* Allow text to wrap instead of truncating */
  white-space: normal;
  word-break: break-word;
}

:deep(.multiselect__option .option) {
  white-space: normal;
  word-break: normal;
}

// Tag-creation option: always shown with highlight colours so it stands out
// as an actionable affordance even before the user arrows onto it.
:deep(.multiselect__option:has(.option-tag-create)) {
  background: color-mix(in srgb, var(--brand) 15%, var(--white));
  color: var(--brand);
}

:deep(.multiselect__option:has(.option-tag-create)::after) {
  background: color-mix(in srgb, var(--brand) 15%, var(--white));
  color: var(--brand);
}

:deep(.multiselect__option .option-tag-create) {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  gap: var(--space-3);
}

:deep(.multiselect__option .option-tag-placeholder) {
  white-space: nowrap;
  flex-shrink: 0;
}
</style>
